<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import clubService from '../services/club.service'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

// Get club UUID from route
const clubUuid = computed(() => route.params.uuid as string)

// Form fields
const clubName = ref('')
const clubShortName = ref('')
const clubNickname = ref('')
const logoFile = ref<File | null>(null)
const currentLogoUrl = ref<string | null>(null)

// Address
const street = ref('')
const buildingNumber = ref('')
const apartmentNumber = ref('')
const city = ref('')
const postalCode = ref('')
const country = ref('PL')

const countryCodes = [
  'AU', 'AT', 'BE', 'BR', 'BG', 'CA', 'HR', 'CY', 'CZ', 'DK',
  'EE', 'FI', 'FR', 'DE', 'GI', 'GR', 'HK', 'HU', 'IN', 'IE',
  'IT', 'JP', 'LV', 'LI', 'LT', 'LU', 'MY', 'MT', 'MX', 'NL',
  'NZ', 'NO', 'PL', 'PT', 'RO', 'SG', 'SK', 'SI', 'ES', 'SE',
  'CH', 'TH', 'AE', 'GB', 'US'
]

// Additional info
const nip = ref('')
const vatUe = ref(false)
const vatPrefix = ref('')

// Contact
const contactEmail = ref('')
const contactPhone = ref('')
const website = ref('')

// Social media
const facebookUrl = ref('')
const instagramUrl = ref('')
const tiktokUrl = ref('')
const timezone = ref('Europe/Warsaw')

// Available timezones
const timezones = [
  { value: 'Europe/Warsaw', label: 'Europe/Warsaw (GMT+1/+2)' },
  { value: 'Europe/London', label: 'Europe/London (GMT+0/+1)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (GMT+1/+2)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (GMT+1/+2)' },
  { value: 'Europe/Madrid', label: 'Europe/Madrid (GMT+1/+2)' },
  { value: 'Europe/Rome', label: 'Europe/Rome (GMT+1/+2)' },
  { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (GMT+1/+2)' },
  { value: 'Europe/Brussels', label: 'Europe/Brussels (GMT+1/+2)' },
  { value: 'Europe/Vienna', label: 'Europe/Vienna (GMT+1/+2)' },
  { value: 'Europe/Prague', label: 'Europe/Prague (GMT+1/+2)' },
  { value: 'Europe/Budapest', label: 'Europe/Budapest (GMT+1/+2)' },
  { value: 'Europe/Bucharest', label: 'Europe/Bucharest (GMT+2/+3)' },
  { value: 'Europe/Athens', label: 'Europe/Athens (GMT+2/+3)' },
  { value: 'Europe/Helsinki', label: 'Europe/Helsinki (GMT+2/+3)' },
  { value: 'Europe/Stockholm', label: 'Europe/Stockholm (GMT+1/+2)' },
  { value: 'Europe/Copenhagen', label: 'Europe/Copenhagen (GMT+1/+2)' },
  { value: 'Europe/Oslo', label: 'Europe/Oslo (GMT+1/+2)' },
  { value: 'Europe/Lisbon', label: 'Europe/Lisbon (GMT+0/+1)' },
  { value: 'Europe/Dublin', label: 'Europe/Dublin (GMT+0/+1)' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow (GMT+3)' },
  { value: 'America/New_York', label: 'America/New_York (GMT-5/-4)' },
  { value: 'America/Chicago', label: 'America/Chicago (GMT-6/-5)' },
  { value: 'America/Denver', label: 'America/Denver (GMT-7/-6)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (GMT-8/-7)' },
  { value: 'America/Toronto', label: 'America/Toronto (GMT-5/-4)' },
  { value: 'America/Mexico_City', label: 'America/Mexico_City (GMT-6/-5)' },
  { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (GMT-3)' },
  { value: 'America/Buenos_Aires', label: 'America/Buenos_Aires (GMT-3)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (GMT+9)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (GMT+8)' },
  { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (GMT+8)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (GMT+8)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GMT+4)' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (GMT+5:30)' },
  { value: 'Asia/Bangkok', label: 'Asia/Bangkok (GMT+7)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (GMT+10/+11)' },
  { value: 'Australia/Melbourne', label: 'Australia/Melbourne (GMT+10/+11)' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (GMT+12/+13)' },
  { value: 'Africa/Cairo', label: 'Africa/Cairo (GMT+2)' },
  { value: 'Africa/Johannesburg', label: 'Africa/Johannesburg (GMT+2)' },
]

const loading = ref(true)
const error = ref<string | null>(null)
const validationErrors = ref<Record<string, string[]>>({})

// Handle logo upload
const handleLogoChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    const file = target.files[0]

    // Check file type
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      error.value = t('club.selectPngOrJpg')
      return
    }

    // Check file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      error.value = t('club.fileTooLarge')
      return
    }

    logoFile.value = file
  }
}

// Handle form submission
const handleSubmit = async () => {
  if (!clubUuid.value) {
    error.value = t('club.errorNoClubUuid')
    return
  }

  loading.value = true
  error.value = null
  validationErrors.value = {}

  try {
    const formData = new FormData()

    // Basic info
    formData.append('name', clubName.value.trim())
    formData.append('short_name', clubShortName.value.trim())
    if (clubNickname.value.trim()) {
      formData.append('nickname', clubNickname.value.trim())
    }

    // Logo
    if (logoFile.value) {
      formData.append('logo', logoFile.value)
    }

    // Address
    if (street.value.trim()) formData.append('street', street.value.trim())
    if (buildingNumber.value.trim()) formData.append('building_number', buildingNumber.value.trim())
    if (apartmentNumber.value.trim()) formData.append('apartment_number', apartmentNumber.value.trim())
    if (city.value.trim()) formData.append('city', city.value.trim())
    if (postalCode.value.trim()) formData.append('postal_code', postalCode.value.trim())
    formData.append('country', country.value)

    // Additional info
    if (nip.value.trim()) formData.append('nip', nip.value.trim())
    formData.append('vat_ue', vatUe.value ? '1' : '0')
    if (vatUe.value && vatPrefix.value.trim()) formData.append('vat_prefix', vatPrefix.value.trim())

    // Contact
    if (contactEmail.value.trim()) formData.append('email', contactEmail.value.trim())
    if (contactPhone.value.trim()) formData.append('phone', contactPhone.value.trim())
    if (website.value.trim()) formData.append('website', website.value.trim())

    // Social media
    if (facebookUrl.value.trim()) formData.append('facebook_url', facebookUrl.value.trim())
    if (instagramUrl.value.trim()) formData.append('instagram_url', instagramUrl.value.trim())
    if (tiktokUrl.value.trim()) formData.append('tiktok_url', tiktokUrl.value.trim())

    // Timezone
    if (timezone.value) formData.append('timezone', timezone.value)

    // Call API to update club
    const response = await clubService.updateClub(clubUuid.value, formData)

    toastStore.success(t('club.clubUpdated'))
    router.push(`/club/${clubUuid.value}`)
  } catch (err: any) {
    console.error('Update club error:', err)

    // Handle validation errors
    if (err.response?.data?.errors) {
      validationErrors.value = err.response.data.errors
    } else {
      error.value = err.response?.data?.message || t('club.errorUpdatingClub')
    }
  } finally {
    loading.value = false
  }
}

// Get validation error for field
const getFieldError = (fieldName: string): string | null => {
  const errors = validationErrors.value[fieldName]
  return errors && errors.length > 0 ? errors[0] : null
}

// Check if field has error
const hasFieldError = (fieldName: string): boolean => {
  return !!validationErrors.value[fieldName]
}

// Handle cancel
const handleCancel = () => {
  router.push(`/club/${clubUuid.value}`)
}

// Fetch club data
const fetchClubData = async () => {
  if (!clubUuid.value) {
    error.value = t('club.errorNoClubUuid')
    loading.value = false
    return
  }

  loading.value = true
  error.value = null

  try {
    const response = await clubService.getClub(clubUuid.value)
    const data = response.club

    // Basic info
    clubName.value = data.name || ''
    clubShortName.value = data.short_name || ''
    clubNickname.value = data.nickname || ''
    currentLogoUrl.value = data.logo_url || null

    // Address
    street.value = data.address?.street || ''
    buildingNumber.value = data.address?.building_number || ''
    apartmentNumber.value = data.address?.apartment_number || ''
    city.value = data.address?.city || ''
    postalCode.value = data.address?.postal_code || ''
    country.value = data.address?.country || 'PL'

    // Additional info
    nip.value = data.nip || ''
    vatUe.value = data.vat_ue || false
    vatPrefix.value = data.vat_prefix || ''

    // Contact
    contactEmail.value = data.contact?.email || ''
    contactPhone.value = data.contact?.phone || ''
    website.value = data.contact?.website || ''

    // Social media
    facebookUrl.value = data.social_media?.facebook_url || ''
    instagramUrl.value = data.social_media?.instagram_url || ''
    tiktokUrl.value = data.social_media?.tiktok_url || ''

    // Timezone
    timezone.value = data.timezone || 'Europe/Warsaw'
  } catch (err: any) {
    console.error('Error fetching club data:', err)
    error.value = err.response?.data?.message || t('club.errorFetchingClub')
  } finally {
    loading.value = false
  }
}

// Initialize
import { onMounted } from 'vue'
onMounted(() => {
  fetchClubData()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="max-w-3xl mx-auto px-4 py-4">
      <div class="mb-4">
        <router-link :to="`/club/${clubUuid}`" class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('club.backToClub') }}
        </router-link>
      </div>

      <!-- Page Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('club.editClub') }}</h1>
        <p class="text-sm text-gray-600 mt-1">{{ t('club.editClubSubtitle') }}</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="bg-white border-2 border-gray-300 rounded shadow-sm p-8 text-center">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
        <p class="text-gray-600">{{ t('club.loadingClubData') }}</p>
      </div>

      <!-- Error Message -->
      <div v-else-if="error" class="mb-4 p-4 bg-red-50 border-2 border-red-300 text-red-700 rounded">
        {{ error }}
      </div>

      <!-- Edit Form -->
      <form v-else @submit.prevent="handleSubmit" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">

        <!-- Basic Information Section -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">{{ t('club.basicInformation') }}</h2>

          <!-- Current Logo -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('club.currentLogo') }}</label>
            <div class="flex md:flex-row flex-col md:items-center gap-4">
              <div class="w-24 h-24 border-2 border-gray-300 rounded flex items-center justify-center bg-white">
                <img
                  v-if="currentLogoUrl"
                  :src="currentLogoUrl"
                  :alt="t('club.logo')"
                  class="w-full h-full object-contain p-2"
                >
                <div v-else class="text-gray-400 text-xs text-center">{{ t('club.noLogo') }}</div>
              </div>
              <div>
                <input
                  type="file"
                  id="logo-input"
                  accept="image/png, image/jpeg, image/jpg"
                  @change="handleLogoChange"
                  class="hidden"
                >
                <label
                  for="logo-input"
                  class="inline-block px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded cursor-pointer hover:bg-blue-700 transition"
                >
                  {{ t('club.uploadNewLogo') }}
                </label>
                <p class="text-xs text-gray-500 mt-2">{{ t('club.logoFileTypes') }}</p>
                <p v-if="logoFile" class="text-xs text-green-600 mt-1">{{ t('club.selectedFile') }}: {{ logoFile.name }}</p>
              </div>
            </div>
          </div>

          <!-- Nazwa klubu -->
          <div class="mb-3">
            <label for="club_name" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('club.clubName') }} <span class="text-red-600">{{ t('common.required') }}</span>
            </label>
            <input
              type="text"
              id="club_name"
              v-model="clubName"
              required
              :class="[
                'w-full px-3 py-2 border rounded text-sm focus:outline-none',
                hasFieldError('name') ? 'border-red-300 focus:border-red-600' : 'border-gray-300 focus:border-blue-600'
              ]"
              :placeholder="t('club.namePlaceholder')"
            >
            <p v-if="hasFieldError('name')" class="mt-1 text-xs text-red-600">
              {{ getFieldError('name') }}
            </p>
          </div>

          <!-- Skrót nazwy klubu -->
          <div class="mb-3">
            <label for="club_short_name" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('club.shortNameClub') }} <span class="text-red-600">{{ t('common.required') }}</span>
            </label>
            <input
              type="text"
              id="club_short_name"
              v-model="clubShortName"
              required
              maxlength="10"
              :class="[
                'w-full px-3 py-2 border rounded text-sm focus:outline-none',
                hasFieldError('short_name') ? 'border-red-300 focus:border-red-600' : 'border-gray-300 focus:border-blue-600'
              ]"
              :placeholder="t('club.shortNamePlaceholder')"
            >
            <p class="text-xs text-gray-500 mt-1">{{ t('club.maxCharacters') }}</p>
            <p v-if="hasFieldError('short_name')" class="mt-1 text-xs text-red-600">
              {{ getFieldError('short_name') }}
            </p>
          </div>

          <!-- Przydomek -->
          <div class="mb-3">
            <label for="club_nickname" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('club.nickname') }}
            </label>
            <input
              type="text"
              id="club_nickname"
              v-model="clubNickname"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              :placeholder="t('club.nicknamePlaceholder')"
            >
          </div>
        </div>

        <!-- Address Section -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">{{ t('club.address') }}</h2>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <!-- Ulica -->
            <div class="md:col-span-2">
              <label for="street" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.street') }}</label>
              <input
                type="text"
                id="street"
                v-model="street"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
                :placeholder="t('club.streetPlaceholder')"
              >
            </div>

            <!-- Numer budynku -->
            <div>
              <label for="building_number" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.buildingNumber') }}</label>
              <input
                type="text"
                id="building_number"
                v-model="buildingNumber"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
                :placeholder="t('club.buildingNumberPlaceholder')"
              >
            </div>

            <!-- Numer lokalu -->
            <div>
              <label for="apartment_number" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.apartmentNumber') }}</label>
              <input
                type="text"
                id="apartment_number"
                v-model="apartmentNumber"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
                :placeholder="t('club.apartmentNumberPlaceholder')"
              >
            </div>

            <!-- Miasto -->
            <div>
              <label for="city" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.city') }}</label>
              <input
                type="text"
                id="city"
                v-model="city"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
                :placeholder="t('club.cityPlaceholder')"
              >
            </div>

            <!-- Kod pocztowy -->
            <div>
              <label for="postal_code" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.postalCode') }}</label>
              <input
                type="text"
                id="postal_code"
                v-model="postalCode"
                pattern="[0-9]{2}-[0-9]{3}"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
                placeholder="00-000"
              >
            </div>
          </div>

          <!-- Kraj -->
          <div class="mt-3">
            <label for="country" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.country') }}</label>
            <select
              id="country"
              v-model="country"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
            >
              <option v-for="code in countryCodes" :key="code" :value="code">
                {{ t(`countries.${code}`) }}
              </option>
            </select>
          </div>
        </div>

        <!-- Additional Information Section -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">{{ t('club.additionalInformation') }}</h2>

          <!-- NIP -->
          <div class="mb-3">
            <label for="nip" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.nip') }}</label>
            <input
              type="text"
              id="nip"
              v-model="nip"
              pattern="[0-9]{10}"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              placeholder="0000000000"
            >
            <p class="text-xs text-gray-500 mt-1">{{ t('club.nipFormat') }}</p>
          </div>

          <!-- VAT UE -->
          <div class="mt-3">
            <label class="inline-flex items-center cursor-pointer">
              <input
                v-model="vatUe"
                type="checkbox"
                class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span class="ml-2 text-sm font-medium text-gray-700">{{ t('club.vatUe') }}</span>
            </label>
          </div>

          <!-- VAT Prefix -->
          <div v-if="vatUe" class="mt-3">
            <label for="vat_prefix" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.vatPrefix') }}</label>
            <input
              type="text"
              id="vat_prefix"
              v-model="vatPrefix"
              maxlength="10"
              :class="[
                'w-full px-3 py-2 border rounded text-sm focus:outline-none',
                hasFieldError('vat_prefix') ? 'border-red-300 focus:border-red-600' : 'border-gray-300 focus:border-blue-600'
              ]"
              :placeholder="t('club.countryCodePlaceholder')"
            >
            <p v-if="hasFieldError('vat_prefix')" class="mt-1 text-xs text-red-600">
              {{ getFieldError('vat_prefix') }}
            </p>
          </div>
        </div>

        <!-- Contact Information Section -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">{{ t('club.contactData') }}</h2>

          <!-- E-mail kontaktowy -->
          <div class="mb-3">
            <label for="contact_email" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.contactEmail') }}</label>
            <input
              type="email"
              id="contact_email"
              v-model="contactEmail"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              placeholder="kontakt@klub.pl"
            >
          </div>

          <!-- Telefon kontaktowy -->
          <div class="mb-3">
            <label for="contact_phone" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.contactPhone') }}</label>
            <input
              type="tel"
              id="contact_phone"
              v-model="contactPhone"
              pattern="[0-9]{9}"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              placeholder="123456789"
            >
            <p class="text-xs text-gray-500 mt-1">{{ t('club.phoneFormat') }}</p>
          </div>

          <!-- Strona www -->
          <div class="mb-3">
            <label for="website" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.website') }}</label>
            <input
              type="url"
              id="website"
              v-model="website"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              placeholder="https://www.klub.pl"
            >
          </div>
        </div>

        <!-- Social Media Section -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">{{ t('club.socialMedia') }}</h2>

          <!-- Facebook URL -->
          <div class="mb-3">
            <label for="facebook_url" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.facebookUrl') }}</label>
            <input
              type="url"
              id="facebook_url"
              v-model="facebookUrl"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              placeholder="https://www.facebook.com/twojklub"
            >
          </div>

          <!-- Instagram URL -->
          <div class="mb-3">
            <label for="instagram_url" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.instagramUrl') }}</label>
            <input
              type="url"
              id="instagram_url"
              v-model="instagramUrl"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              placeholder="https://www.instagram.com/twojklub"
            >
          </div>

          <!-- TikTok URL -->
          <div class="mb-3">
            <label for="tiktok_url" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.tiktokUrl') }}</label>
            <input
              type="url"
              id="tiktok_url"
              v-model="tiktokUrl"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              placeholder="https://www.tiktok.com/@twojklub"
            >
          </div>

          <!-- Timezone -->
          <div class="mb-3">
            <label for="timezone" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.timezone') }}</label>
            <select
              id="timezone"
              v-model="timezone"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
            >
              <option v-for="tz in timezones" :key="tz.value" :value="tz.value">
                {{ tz.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Submit Section -->
        <div class="pt-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            @click="handleCancel"
            class="py-2 px-4 bg-gray-100 text-gray-700 font-medium rounded hover:bg-gray-200 border border-gray-300"
            :disabled="loading"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            type="submit"
            class="py-2 px-4 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="loading"
          >
            {{ loading ? t('coaches.saving') : t('coaches.saveChanges') }}
          </button>
        </div>
      </form>
    </div>
  </main>
</template>
